/**
 * Kontekst Gemmy w czacie wewnętrznym (DM / kanał), osobno od draweru AIDialog.
 * Historia tur (user/assistant) w localStorage — max **30 min** od ostatniej odpowiedzi Gemmy; używana tylko gdy user **ponownie** napisze `@gemma` (nie ma trybu „Gemma odpowiada na zwykłe wiadomości”).
 */

import type { ChatMessage } from '../services/deepseekService';

const STORAGE_PREFIX = 'ic:gemma:v1:';
const SESSION_TTL_MS = 30 * 60 * 1000;
const MAX_MESSAGES = 24; /* user+assistant łącznie */

export type GemmaTurn = { role: 'user' | 'assistant'; content: string };

type Store = { exp: number; turns: GemmaTurn[] };

function key(convId: string): string {
  return `${STORAGE_PREFIX}${convId}`;
}

function readStore(convId: string): Store | null {
  try {
    const raw = localStorage.getItem(key(convId));
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (typeof p.exp !== 'number' || !Array.isArray(p.turns)) return null;
    if (Date.now() > p.exp) {
      localStorage.removeItem(key(convId));
      return null;
    }
    return p as Store;
  } catch {
    return null;
  }
}

export function loadGemmaTurnsForApi(convId: string): ChatMessage[] {
  const s = readStore(convId);
  if (!s) return [];
  return s.turns.map((t) => ({ role: t.role, content: t.content }));
}

/**
 * Ostatni `project_id` wyciągnięty z odpowiedzi Gemmy (np. „(ID: 232-233)”) — utrwala kontekst projektu między turami czatu.
 */
export function extractStickyProjectIdFromGemmaTurns(convId: string): string | null {
  const s = readStore(convId);
  if (!s) return null;
  const re = /\(ID:\s*`?([0-9A-Za-z][0-9A-Za-z._-]*)`?\)/gi;
  let last: string | null = null;
  for (const t of s.turns) {
    if (t.role !== 'assistant') continue;
    const c = t.content;
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(c)) !== null) {
      const v = m[1].trim();
      if (v) last = v;
    }
  }
  return last;
}

export function appendGemmaExchange(convId: string, userContent: string, assistantContent: string): void {
  const prev = readStore(convId);
  const turns: GemmaTurn[] = [
    ...(prev?.turns ?? []),
    { role: 'user', content: userContent },
    { role: 'assistant', content: assistantContent },
  ];
  const trimmed = turns.slice(-MAX_MESSAGES);
  const exp = Date.now() + SESSION_TTL_MS;
  try {
    localStorage.setItem(key(convId), JSON.stringify({ exp, turns: trimmed }));
  } catch {}
}

export function clearGemmaSession(convId: string): void {
  try {
    localStorage.removeItem(key(convId));
  } catch {}
}

export function isGemmaSessionActive(convId: string): boolean {
  return readStore(convId) != null;
}

/** Przedłuż ważność sesji Gemmy przy wejściu na wątek — unika „zgubionej” kontynuacji bez @gemma po krótkiej przerwie. */
export function bumpGemmaSessionExpiryIfActive(convId: string): void {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(key(convId));
  } catch {
    return;
  }
  if (!raw) return;
  try {
    const p = JSON.parse(raw) as { exp?: unknown; turns?: unknown };
    if (typeof p.exp !== 'number' || !Array.isArray(p.turns)) return;
    if (Date.now() > p.exp) {
      try {
        localStorage.removeItem(key(convId));
      } catch {}
      return;
    }
    localStorage.setItem(key(convId), JSON.stringify({ exp: Date.now() + SESSION_TTL_MS, turns: p.turns }));
  } catch {}
}

/** Jak InternalChatPanel — wyciąga treść odpowiedzi poniżej cytatu. */
function parseReplyBody(text: string): { replyText: string } | null {
  const parts = text.split(/\n\n+/);
  if (parts.length < 2) return null;
  const replyText = parts.slice(1).join('\n\n').trim();
  if (!replyText) return null;
  const firstPart = parts[0];
  if (!firstPart.startsWith('> ')) return null;
  return { replyText };
}

/** Znaki niewidzialne / BOM po wstawieniu @gemma z pickera — bez tego regex czasem nie widzi wzmianki. */
function normalizeInternalChatBodyForGemma(s: string): string {
  return s
    .replace(/\u200b|\u200c|\u200d|\ufeff/g, '')
    .replace(/\u00a0/g, ' ')
    .trim();
}

/**
 * Zwraca treść pytania do API Gemmy albo null = zwykła wiadomość czatu.
 * **Wyłącznie** gdy w treści (lub w treści pod cytatem) jest `@gemma` — bez trybu „kontynuacja sesji bez wzmianki”.
 */
export function extractGemmaQueryForSend(raw: string, convId: string): string | null {
  void convId; /* zachowany w sygnaturze — ewentualne rozszerzenia per wątek */
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const replyParsed = parseReplyBody(trimmed);
  const bodyRaw = replyParsed ? replyParsed.replyText.trim() : trimmed;
  const body = normalizeInternalChatBodyForGemma(bodyRaw);

  if (/@gemma\b/i.test(body)) {
    const m = body.match(/@gemma\b([\s\S]*)/i);
    let q = (m?.[1] ?? '').trim().replace(/^[,:.-]\s*/i, '').trim();
    /* Same @gemma z pickera bez treści — wcześniej zwracaliśmy null i Gemma w ogóle nie startowała. */
    return q.length ? q : 'Jak mogę pomóc?';
  }

  return null;
}
